/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ClientGeoArea } from '@DigitaleDoerfer/digitale-doerfer-api';
import React, { FC, useCallback, useMemo } from 'react';
import ExpandIcon from '@mui/icons-material/AddBoxOutlined';
import EndIcon from '@mui/icons-material/CancelOutlined';
import CollapseIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import { TreeView } from '@mui/x-tree-view';
import { useStore } from '../store';
import useFetchGeoAreasFirstLevel from './hooks/useFetchGeoAreasFirstLevel';
import useFetchSelectedContractGeoAreas from './hooks/useFetchSelectedContractGeoAreas';
import useGeoArea from './hooks/useGeoArea';
import TreeNode from './TreeNode';
import { CircularProgress } from '@mui/material';

const GeoAreaTree: FC = () => {
	const { handleExpandedGeoAreaIds, setSelectedGeoAreaId } = useStore();
	const expandedGeoAreaIds = useStore(state => state.expandedGeoAreaIds);
	const selectedGeoAreaId = useStore(state => state.selectedGeoAreaId);
	const { getGeoAreasByParentId } = useGeoArea(selectedGeoAreaId);

	const firstLevelGeoAreas = getGeoAreasByParentId(null);

	const { isLoading } = useFetchGeoAreasFirstLevel();
	const { isLoading: isLoadingSelectedContractGeoAreas } = useFetchSelectedContractGeoAreas();

	const handleGeoAreaLabelClick = useCallback(
		(geoAreaId: ClientGeoArea['id']) => {
			setSelectedGeoAreaId(geoAreaId);
		},
		[setSelectedGeoAreaId]
	);

	const handleGeoAreaExpandClick = useCallback(
		(geoAreaId: ClientGeoArea['id']) => {
			handleExpandedGeoAreaIds(geoAreaId);
			setSelectedGeoAreaId(geoAreaId);
		},
		[handleExpandedGeoAreaIds, setSelectedGeoAreaId]
	);

	const childNodes = useMemo(
		() =>
			firstLevelGeoAreas.map(geoArea => {
				return (
					<TreeNode
						key={geoArea?.id}
						geoArea={geoArea}
						selectedGeoAreaId={selectedGeoAreaId}
						onNodeLabelClick={handleGeoAreaLabelClick}
						onNodeExpandClick={handleGeoAreaExpandClick}
					/>
				);
			}),
		[firstLevelGeoAreas, handleGeoAreaLabelClick, handleGeoAreaExpandClick, selectedGeoAreaId]
	);

	if (isLoading || isLoadingSelectedContractGeoAreas) {
		return <CircularProgress />;
	}

	return (
		<TreeView
			defaultCollapseIcon={<CollapseIcon />}
			defaultExpandIcon={<ExpandIcon />}
			defaultEndIcon={<EndIcon color={'disabled'} />}
			expanded={expandedGeoAreaIds}
		>
			{childNodes}
		</TreeView>
	);
};
export default GeoAreaTree;
